-- | Implements the shared behavior of selecting items from a list with checkboxes.
module Internal.SelectionMap where

import Common

import Concur.React.Props (ReactProps)
import Data.List (List)
import Data.Map (Map)
import Data.Map as Map
import Widgets.Checkbox (checkbox)

type SelectionMap k v =
    { sel :: Map k v
    }

type HasID k r = { id :: k | r }

empty :: forall k v. SelectionMap k v
empty = { sel: Map.empty }

fromFoldable :: forall arr k v. Ord k => Foldable arr =>
    arr (Tuple k v) -> SelectionMap k v
fromFoldable arr = { sel: Map.fromFoldable arr }

fromFoldable' :: forall arr k v. Ord k => Functor arr => Foldable arr =>
    arr (HasID k v) -> SelectionMap k (HasID k v)
fromFoldable' = fromFoldable <<< map (\v -> Tuple v.id v)

select :: forall k v. Ord k => k -> v -> SelectionMap k v -> SelectionMap k v
select k v s = s { sel = Map.insert k v s.sel }

-- | `HasID` variant of `select`
select' :: forall k v. Ord k => HasID k v -> SelectionMap k (HasID k v) -> SelectionMap k (HasID k v)
select' v = select v.id v


deselect :: forall k v. Ord k => k -> SelectionMap k v -> SelectionMap k v
deselect k s = s { sel = Map.delete k s.sel }

setSelected :: forall k v. Ord k => k -> v -> SelectionMap k v -> Boolean -> SelectionMap k v
setSelected k v sel true = select k v sel
setSelected k _ sel false = deselect k sel

isSelected :: forall k v. Ord k => k -> SelectionMap k v -> Boolean
isSelected k { sel } = k `Map.member` sel

getSelected :: forall k v. Ord k => k -> SelectionMap k v -> Maybe v
getSelected k { sel } = Map.lookup k sel

areAllSelected :: forall arr k v. Ord k => Foldable arr => arr k -> SelectionMap k v -> Boolean
areAllSelected ks { sel } = all (_ `Map.member` sel) ks

isEmpty :: forall k v. Ord k => SelectionMap k v -> Boolean
isEmpty { sel } = Map.isEmpty sel

selectAll :: forall arr k v. Ord k => Foldable arr =>
    arr (Tuple k v) -> SelectionMap k v -> Boolean -> SelectionMap k v
selectAll ks s true =
    s { sel = Map.union s.sel $ Map.fromFoldable ks }
selectAll ks s false =
    s { sel = Map.difference s.sel $ Map.fromFoldable ks }

-- | `HasID` variant of `selectAll`.
selectAll' :: forall arr k v. Ord k => Functor arr => Foldable arr =>
    arr (HasID k v) -> SelectionMap k (HasID k v) -> Boolean -> SelectionMap k (HasID k v)
selectAll' vs = selectAll $ map (\v -> Tuple v.id v) vs

listSelected :: forall k v. SelectionMap k v -> List v
listSelected { sel } = Map.values sel


---- Checkboxes ----

-- | Renders a checkbox using the given render callback. State will be
-- | determined by whether the checkbox is checked in the given selection map.
selectionCheckbox :: forall k v.
    Ord k => k -> v
    -> (Boolean -> Widget HTML Boolean)
    -> SelectionMap k v
    -> Widget HTML (SelectionMap k v)
selectionCheckbox k v renderCheck sel =
    setSelected k v sel <$> renderCheck (isSelected k sel)

selectionCheckbox' :: forall k v.
    Ord k => k -> v
    -> Array (ReactProps Boolean)
    -> SelectionMap k v
    -> Widget HTML (SelectionMap k v)
selectionCheckbox' k v props =
    selectionCheckbox k v $ checkbox props


selectAllCheckbox :: forall arr k v.
    Functor arr => Foldable arr =>
    Ord k => arr (Tuple k v)
    -> (Boolean -> Widget HTML Boolean)
    -> SelectionMap k v
    -> Widget HTML (SelectionMap k v)
selectAllCheckbox items renderCheck sel =
    selectAll items sel <$> renderCheck (areAllSelected itemKeys sel)
  where
    itemKeys = map fst items

selectAllCheckbox' :: forall arr k v.
    Ord k => Functor arr => Foldable arr =>
    arr (HasID k v)
    -> (Boolean -> Widget HTML Boolean)
    -> SelectionMap k (HasID k v)
    -> Widget HTML (SelectionMap k (HasID k v))
selectAllCheckbox' items =
    selectAllCheckbox (map (\v -> Tuple v.id v) items)
